import React, { useState } from 'react'
import _ from 'lodash'
import Modal from './Modal'
import Tooltip from './Tooltip'
import NewUserEditor from './NewUserEditor'
import api from '../api'
import { saveClient } from '../fire'
import { billingFrequencies } from '@evergreen/shared/constants'
import Select, { SelectCreatable } from './Select'

let autoReportOptions = [
  { label: 'Daily', value: 'dailyReportEnabled' },
  { label: 'Weekly', value: 'weeklyReportEnabled' },
  { label: 'Monthly', value: 'monthlyReportEnabled' },
]

let ClientEditor = ({ client, clients, onClose, newClient = false }) => {
  let initialState = {
    id: '',
    name: '',
    parseMappings: [
      {
        type: 'form',
        pattern: '',
      },
    ],
    users: null,
    billingType: '',
    pricePerLead: 100,
    billingFrequency: null,
    billingClientId: '',
    sendLeadEmailDefault: true,
    cityDropdownOptions: '',
    dailyReportEnabled: false,
    weeklyReportEnabled: false,
    monthlyReportEnabled: false,
    reportClientIds: [],
    reportEmailAddresses: [],
    webhookUrl: '',
    ..._.cloneDeep(client),
  }
  let [addingNewUser, setAddingNewUser] = useState(false)
  let [deletingUserId, setDeletingUserId] = useState('')
  let [state, setState] = useState(initialState)
  let [saving, setSaving] = useState(false)
  // lodash path, like ['users', 'uid_345', 'name'] or a string 'users.uid_345.name'
  let set = (path, value) => setState(_.set(_.cloneDeep(state), path, value))
  let {
    name,
    users,
    cityDropdownOptions,
    parseMappings,
    id,
    pricePerLead,
    billingType,
    billingFrequency,
    billingClientId,
    sendLeadEmailDefault,
    reportClientIds,
    reportEmailAddresses,
    webhookUrl,
  } = state
  let childBillingClientIds = _.filter(
    clients,
    c => Boolean(id) && c.billingClientId === id && id !== c.id
  ).map(({ id }) => id)
  let hasChildClientIds = !_.isEmpty(childBillingClientIds)

  return (
    <Modal
      isOpen
      title={newClient ? 'New Client' : 'Edit Client'}
      maxWidth="96%"
      onClose={() => onClose()}
      body={
        <div className="form">
          <div className="form-group">
            <div className="row">
              <div className="col-3">
                <label>Name</label>
                <input
                  onChange={e => set('name', e.target.value)}
                  className="form-control form-control-sm"
                  value={name}
                />
              </div>
              <div className="col-3">
                <label>ID</label>
                <input
                  readOnly={!newClient}
                  onChange={e => {
                    let val = e.target.value
                    setState(prev => ({
                      ...prev,
                      id: val,
                      billingClientId: val,
                    }))
                  }}
                  className="form-control form-control-sm"
                  value={id}
                />
              </div>
              <div className="col-3">
                <label>Billing Client ID</label>
                <select
                  disabled={!id || hasChildClientIds}
                  onChange={e => set('billingClientId', e.target.value)}
                  className="form-control form-control-sm"
                  value={billingClientId}
                >
                  <option value="">{billingClientId}</option>
                  {clients.map(({ id: clientId }) => (
                    <option key={clientId} value={clientId}>
                      {clientId}
                    </option>
                  ))}
                </select>
              </div>

              {hasChildClientIds && (
                <div className="col-3">
                  <label>Sub-Client IDs</label>
                  <input
                    className="form-control form-control-sm"
                    readOnly
                    value={childBillingClientIds.join(', ')}
                  />
                </div>
              )}
            </div>
          </div>
          <div className="form-group">
            <div className="row">
              <div className="col-3">
                <label>Billing Type</label>
                <select
                  onChange={e => set('billingType', e.target.value)}
                  className="form-control form-control-sm"
                  value={billingType}
                >
                  <option value="">-- Select billing type --</option>
                  {['fixed', 'perLead'].map(o => (
                    <option key={o} value={o}>
                      {_.startCase(o)}
                    </option>
                  ))}
                </select>
              </div>
              {billingType === 'perLead' && (
                <>
                  <div className="col-3">
                    <label>Cost Per Lead</label>
                    <input
                      onChange={e =>
                        set('pricePerLead', parseFloat(e.target.value))
                      }
                      className="form-control form-control-sm"
                      value={pricePerLead}
                      type="number"
                    />
                  </div>
                  <div className="col-3">
                    <label>Billing Frequency</label>
                    {billingClientId !== id ? (
                      <select
                        disabled
                        className="form-control form-control-sm"
                        value={billingFrequency || ''}
                      >
                        <option value="">
                          (Determined by {billingClientId})
                        </option>
                      </select>
                    ) : (
                      <select
                        onChange={e => set('billingFrequency', e.target.value)}
                        className="form-control form-control-sm"
                        value={billingFrequency || ''}
                      >
                        <option value="">-- Select billing type --</option>
                        {[
                          billingFrequencies.semimonthly,
                          billingFrequencies.monthly,
                        ].map(o => (
                          <option key={o} value={o}>
                            {_.startCase(o)}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                </>
              )}
              <div className="col-3">
                <label>Send Lead Email (Default)</label>
                <div>
                  <input
                    type="checkbox"
                    onChange={e =>
                      set('sendLeadEmailDefault', e.target.checked)
                    }
                    checked={sendLeadEmailDefault}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="form-group">
            <div className="row">
              <div className="col-3">
                <label>Auto Reports</label>
                <Select
                  isMulti
                  placeholder="-- Select to enable --"
                  options={_.map(autoReportOptions, 'value')}
                  onChange={selectedStateKeys => {
                    setState(prev => ({
                      ...prev,
                      ...autoReportOptions.reduce((acc, { value }) => {
                        acc[value] = _.includes(selectedStateKeys, value)
                        return acc
                      }, {}),
                    }))
                  }}
                  getOptionLabel={value =>
                    _.find(autoReportOptions, { value }).label
                  }
                  value={autoReportOptions
                    .filter(o => state[o.value])
                    .map(o => o.value)}
                />
              </div>
              <div className="col-4">
                <label>Report Client IDs</label>
                <Select
                  isMulti
                  options={_.map(clients, 'id')}
                  getOptionLabel={v => v}
                  onChange={ids => set('reportClientIds', ids)}
                  value={_.sortBy(reportClientIds)}
                />
              </div>
              <div className="col-5">
                <label>Report Email Addresses</label>
                <div>
                  <SelectCreatable
                    options={[]}
                    isMulti
                    className="bg-white"
                    placeholder="Add email addresses"
                    onChange={newValue => {
                      set(
                        'reportEmailAddresses',
                        !newValue ? [] : newValue.map(r => r.value)
                      )
                    }}
                    value={_.chain(reportEmailAddresses)
                      .uniq()
                      .compact()
                      .map(value => ({ label: value, value }))
                      .value()}
                    noOptionsMessage={() => 'Enter Email Address'}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="form-group">
            <label>Users</label>
            {_.map(users, (user, userId) =>
              !user ? null : (
                <div className="row mb-1 mr-0" key={userId}>
                  <div className="col pr-0">
                    <input
                      onChange={e =>
                        set(['users', userId, 'name'], e.target.value)
                      }
                      className="form-control form-control-sm"
                      placeholder="Name"
                      value={user.name}
                    />
                  </div>
                  <div className="col pr-0">
                    <input
                      className="form-control form-control-sm"
                      placeholder="Phone number"
                      onChange={e =>
                        set(['users', userId, 'phoneNumber'], e.target.value)
                      }
                      value={user.phoneNumber}
                    />
                  </div>
                  <div className="col pr-0">
                    <input
                      className="form-control form-control-sm"
                      readOnly
                      value={user.email}
                    />
                  </div>
                  <div className="input-group ml-2" style={{ width: '130px' }}>
                    <div className="input-group-prepend">
                      <span className="input-group-text">
                        <i className="fa fa-tag" />
                      </span>
                    </div>
                    <input
                      className="form-control form-control-sm"
                      placeholder="Tag"
                      value={user.tag || ''}
                      onChange={e =>
                        set(['users', userId, 'tag'], e.target.value)
                      }
                    />
                  </div>
                  <div className="col">
                    <Tooltip
                      title="Always include this user's email address on leads with 'sent' status"
                      style={{ display: 'inline-block' }}
                      topOffset={10}
                      wrap
                    >
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() =>
                          set(
                            ['users', userId, 'excludeFromLeadNotifyEmail'],
                            !user.excludeFromLeadNotifyEmail
                          )
                        }
                      >
                        <i className="fa fa-paper-plane" /> Include{' '}
                        <div
                          className="d-inline-block"
                          style={{ width: '25px' }}
                        >
                          {!user.excludeFromLeadNotifyEmail ? (
                            <i className="fa fa-check" />
                          ) : (
                            <i className="fa fa-remove" />
                          )}
                        </div>
                      </button>
                    </Tooltip>
                    <button
                      className="ml-2 btn btn-danger btn-sm"
                      disabled={deletingUserId === userId}
                      onClick={async () => {
                        if (
                          window.confirm(
                            'Are you sure you want to delete the user?'
                          )
                        ) {
                          setDeletingUserId(userId)
                          try {
                            await api('/delete-user', {
                              clientId: id,
                              uid: userId,
                            })
                            set(['users', userId], undefined)
                          } catch (e) {
                            console.error(e)
                            window.alert(e)
                          }
                          setDeletingUserId('')
                        }
                      }}
                    >
                      <i className="fa fa-remove" />
                    </button>
                  </div>
                </div>
              )
            )}
            {addingNewUser ? (
              <NewUserEditor
                clientId={id}
                onClose={() => setAddingNewUser(false)}
                onSavedNewUser={newUser => {
                  setAddingNewUser(false)
                  set(
                    ['users', newUser.uid],
                    _.pick(newUser, [
                      'email',
                      'name',
                      'phoneNumber',
                      'tag',
                      'excludeFromLeadNotifyEmail',
                    ])
                  )
                }}
              />
            ) : (
              <div className="my-2">
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setAddingNewUser(true)}
                >
                  <i className="fa fa-plus" /> Add User
                </button>
              </div>
            )}
          </div>
          <div className="mb-2">Parse Mappings</div>
          <div
            className="form-group"
            style={{ maxHeight: '200px', overflow: 'auto' }}
          >
            {_.map(parseMappings, ({ type, pattern }, key) => (
              <div className="row mb-1 mr-0" key={key}>
                <div className="col-5 pr-0">
                  <select
                    onChange={e =>
                      set(['parseMappings', key, 'type'], e.target.value)
                    }
                    className="form-control form-control-sm"
                    value={type}
                  >
                    {['form', 'phone'].map(t => (
                      <option key={t} value={t}>
                        {_.capitalize(t)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-6 pr-0">
                  <input
                    onChange={e =>
                      set(['parseMappings', key, 'pattern'], e.target.value)
                    }
                    className="form-control form-control-sm"
                    placeholder="Text"
                    value={pattern}
                  />
                </div>
                <div className="col-1">
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => {
                      setState(prev => ({
                        ...prev,
                        parseMappings: prev.parseMappings.filter(
                          (pm, index) => key !== index
                        ),
                      }))
                    }}
                  >
                    <i className="fa fa-remove" />
                  </button>
                </div>
              </div>
            ))}
            <div className="my-2">
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => {
                  setState(prev => ({
                    ...prev,
                    parseMappings: (prev.parseMappings || []).concat({
                      type: 'form',
                      pattern: '',
                    }),
                  }))
                }}
              >
                <i className="fa fa-plus" /> Add Mapping
              </button>
            </div>
          </div>
          <div className="row mb-2">
            <div className="col-6">
              <div className="my-2">City Dropdown Options</div>
              <textarea
                placeholder="One city per line"
                rows={3}
                className="form-control form-control-sm"
                onChange={e => set('cityDropdownOptions', e.target.value)}
                value={cityDropdownOptions}
              />
            </div>
            <div className="col-6">
              <div className="my-2">
                Webhook URL{' '}
                <Tooltip
                  title="If provided, lead info will get POSTed to this URL after the lead is parsed"
                  style={{ display: 'inline-block' }}
                >
                  <i className="fa fa-info-circle" />
                </Tooltip>
              </div>
              <input
                className="form-control form-control-sm"
                onChange={e => set('webhookUrl', e.target.value)}
                value={webhookUrl}
              />
            </div>
          </div>
        </div>
      }
      footer={() => (
        <div>
          <button
            className="btn btn-danger"
            disabled={saving}
            onClick={() => onClose()}
          >
            Cancel
          </button>
          <button
            className="ml-2 btn btn-success"
            disabled={saving || addingNewUser}
            onClick={async () => {
              setSaving(true)
              try {
                await saveClient({
                  oldClient: client,
                  client: {
                    ...state,
                    users: _.pickBy(state.users, u => !_.isUndefined(u)), // cuz they could be undefined when deleted.  could be done prettier eh?
                  },
                })
                onClose()
              } catch (e) {
                setSaving(false)
                console.log('error saving client!', e)
                alert(e.message)
              }
            }}
          >
            Save {saving && <i className="fa fa-spinner fa-spin" />}
          </button>
        </div>
      )}
    />
  )
}

export default ClientEditor
